import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Link2, Siren } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { excerpt, fill, prayerExcerptChars } from "@/lib/prayers";
import { ChainCounters, PrayMinuteButton, URGENT_COLOR } from "@/components/prayers/PrayerChain";
import { ChainPeopleButton } from "@/components/prayers/PrayerChainAuthor";
import { PrayerLinkShare } from "@/components/prayers/PrayerLinkShare";
import { DismissX } from "@/components/smart/DismissX";

const POLL_MS = 10000;

// Données (exportées pour les cartes intelligentes) : demandes urgentes actives, rafraîchies toutes les 10 s.
export const usePrayerChain = (enabled = true) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/prayers/chain/active").then(({ data }) => setItems(data.enabled === false ? [] : (data.items || []))).catch(() => setItems([])), []);
  useEffect(() => {
    if (!enabled) return undefined;
    load();
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, POLL_MS);
    window.addEventListener("yalola:prayer_changed", load);
    return () => { clearInterval(id); window.removeEventListener("yalola:prayer_changed", load); };
  }, [enabled, load]);
  return { items, load };
};

// Top-of-feed banner: the most recent active urgent request + live chain counters, « Prie 1 minute maintenant » inline.
export const PrayerChainBanner = ({ data = null, onDismiss = null }) => {
  const { t, config } = useApp();
  const feats = config?.settings?.features || {};
  const enabled = feats.prayers !== false && feats.prayer_chain !== false;
  const own = usePrayerChain(enabled && !data);
  const { items, load } = data || own;

  if (!enabled || !items || !items.length) return null;
  const p = items[0];
  const name = p.author?.name || "";
  return (
    <div className="yalola-card p-4 space-y-3" style={{ borderColor: URGENT_COLOR + "66", backgroundImage: `radial-gradient(circle at 100% 0%, ${URGENT_COLOR}22 0, transparent 120px)` }} data-testid="prayer-chain-banner" data-count={items.length} data-mine={p.is_mine ? "1" : "0"}>
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: URGENT_COLOR + "1f" }}><Siren size={16} color={URGENT_COLOR} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide inline-flex items-center gap-1" style={{ color: URGENT_COLOR }}><Link2 size={11} /> {p.is_mine ? t("prayers.chain_mine_title", "Ta chaîne de prière") : t("prayers.chain_title", "Chaîne de prière")}</div>
          <div className="text-sm font-semibold truncate" data-testid="prayer-chain-banner-title">
            {p.is_mine ? t("prayers.chain_mine_banner", "Ta chaîne de prière est en marche") : fill(t("prayers.chain_needs", "{name} a besoin de prière maintenant"), { name })}
          </div>
        </div>
        {items.length > 1 && <span className="chip text-[10px]" data-testid="prayer-chain-banner-more">+{items.length - 1}</span>}
        <DismissX onClick={onDismiss} testId="prayer-chain-dismiss" label={t("smart_cards.dismiss", "Masquer")} />
      </div>
      <p className="text-sm leading-snug whitespace-pre-wrap" style={{ color: "var(--yalola-text)" }} data-testid="prayer-chain-banner-text">{excerpt(p.content, prayerExcerptChars(config))}</p>
      <ChainCounters chain={p.chain} compact testId="prayer-chain-banner-counters" />
      <div className="flex items-center gap-2 flex-wrap">
        {/* Lot 656 : l'auteur ne prie pas pour lui-même — il voit qui a prié et partage sa demande */}
        {p.is_mine ? <><ChainPeopleButton p={p} onThanked={load} /><PrayerLinkShare p={p} /></> : <PrayMinuteButton p={p} onCompleted={load} />}
        <Link to={`/prayers?focus=${p.id}`} className="text-xs font-semibold inline-flex items-center gap-0.5 hover:underline" style={{ color: "var(--yalola-text-muted)" }} data-testid="prayer-chain-banner-link">
          {t("prayers.chain_see", "Voir la demande")} <ChevronRight size={14} />
        </Link>
      </div>
    </div>
  );
};
